<template>
  <div v-if="loading" class="d-flex justify-content-center p-4">
    <b-spinner label="Loading..." variant="dark" />
  </div>

  <div v-else>
    <base-bread-crumb :items="breadcrumbs" />

    <div
      v-if="learningUnit"
      ref="learning-unit-name"
      class="h5 display-4 mt-1 mb-5 font-weight-bold"
    >
      {{ learningUnit.name }}
    </div>

    <div ref="action-title" class="h5 font-weight-bold mb-4">
      {{ postId ? 'Edit task part' : 'Create task part' }}
      {{ ' ' + `${partMetaData ? partMetaData.index + ' ' + partMetaData.name : ''}` }}
    </div>

    <!-- Title -->
    <div ref="input__task-title" class="mt-4">
      <label for="input-title"> Title </label>
      <b-form-input
        id="input-title"
        v-model.trim="$v.form.title.$model"
        type="text"
        placeholder="Insert your title"
        :disabled="isSubmitting"
        :state="$v.form.title.$anyError ? false : null"
        aria-describedby="input-title-invalid-feedback"
        class="mt-2 input__task-title"
      />
      <b-form-invalid-feedback id="input-title-invalid-feedback" class="mt-2">
        {{ titleErrorMessage }}
      </b-form-invalid-feedback>
    </div>

    <!-- Task -->
    <div ref="input__task-description" class="mt-4">
      <b-form-group>
        <label id="quill-editor-label"> Task </label>
        <div>
          <base-text-editor
            id="input-post-description"
            ref="myTextEditor"
            v-model="$v.form.description.$model"
            :disabled="isSubmitting"
            placeholder="Insert your task"
            class="mt-2 input__task-description ql-editor--large"
            :class="{
              text_editor__required: !$v.form.description.$model && $v.form.description.$anyDirty
            }"
          ></base-text-editor>
        </div>
        <div
          v-if="!$v.form.description.$model && $v.form.description.$anyDirty"
          class="small text-error mt-1"
        >
          Task must be at least 1 character.
        </div>
      </b-form-group>
    </div>

    <!-- Attachment -->
    <div class="dropzone-attactment mt-4">
      <label for="input-attachments"> Attachment </label>
      <base-s3-uploader
        id="input-attachments"
        ref="attachmentUploader"
        v-model="file.attachments"
        :reference-meta="{ ...referenceMeta, field: 'attachments' }"
        :disabled="isSubmitting"
        :is-public="false"
        class="mt-2"
      >
      </base-s3-uploader>
    </div>

    <!-- Assessment Rubric -->
    <base-form-criteria
      ref="form-criteria"
      :mode="mode"
      class="mt-4"
      :criteria.sync="criteria"
      :trigger-validation="triggerValidation"
      :disabled="isSubmitting"
      :has-user-enrolled="hasUserEnrolled"
      @add:criteria="onAddCriteria"
      @delete:criteria="onDeleteCriteria"
    />

    <!-- Word limit -->
    <div ref="input__word-limit" class="mt-4">
      <label for="input-word-limit">
        Word limit <span class="text-black-50">(Optional)</span></label
      >
      <b-form-input
        id="input-word-limit"
        v-model.number="$v.form.word_limit.$model"
        type="number"
        :disabled="isSubmitting"
        :state="$v.form.word_limit.$anyError ? false : null"
        aria-describedby="input-word-limit-invalid-feedback"
        class="mt-2 col-2 input__word-limit"
      />

      <b-form-invalid-feedback
        v-if="$v.form.word_limit.required"
        id="input-word-limit-invalid-feedback"
        class="mt-2"
      >
        Word limit must be greater than 0
      </b-form-invalid-feedback>
    </div>

    <div class="flex flex-row py-u5 border-top border-muted mt-u11">
      <b-button
        variant="success"
        class="btn__create_or_edit"
        :disabled="isSubmitting"
        @click="submit"
      >
        <div v-if="isSubmitting" class="mb-u1">
          <b-spinner label="Loading..." variant="secondary" small />
        </div>
        <div v-else>{{ postId ? 'Save' : 'Create' }}</div>
      </b-button>

      <base-back-to-previous-button variant="link" :show-arrow="false" :disabled="isSubmitting" />
    </div>
  </div>
</template>

<script>
import Post from '@/models/Post';
import Criteria from '@/models/Criteria';
import BaseBreadCrumb from '@/components/base-components/BaseBreadCrumb';
import BaseTextEditor from '@/components/base-components/BaseTextEditor';
import BaseS3Uploader from '@/components/base-components/BaseS3Uploader';
import BaseFormCriteria from '@/components/base-components/BaseFormCriteria';
import BaseBackToPreviousButton from '@/components/base-components/BaseBackToPreviousButton';
import { learningUnitInfoMixin } from '@/mixins/learningUnitInfoMixin';
import { required, requiredIf, minLength, maxLength, minValue } from 'vuelidate/lib/validators';
import Application from '@/models/Application';

export default {
  components: {
    BaseBreadCrumb,
    BaseTextEditor,
    BaseS3Uploader,
    BaseFormCriteria,
    BaseBackToPreviousButton
  },
  mixins: [learningUnitInfoMixin],

  props: {
    postId: {
      type: [String, Number],
      default: null
    },

    part: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      loading: false,
      isSubmitting: false,
      form: {
        title: '',
        description: '',
        word_limit: ''
      },
      file: {
        attachments: []
      },

      referenceMeta: {
        ref: 'post',
        refId: null
      },
      criteria: new Criteria({
        pass_title: 'Pass',
        pass_criterion: 'Reached the passing criteria.',
        not_yet_title: 'Not Yet',
        not_yet_criterion: 'Not yet reached the passing criteria.',
        additional_criteria:
          this.part === 'WORK_EXAMPLE'
            ? [
                {
                  title: 'Almost',
                  description: 'Almost reached the passing criteria.'
                }
              ]
            : []
      }),
      additional_criteria_delete_ids: [],
      triggerValidation: false,
      hasUserEnrolled: false
    };
  },

  computed: {
    mode() {
      return this.part && this.part === 'WORK_EXAMPLE' ? 'additional-criteria' : 'criteria';
    },

    partMetaData() {
      const part = {};
      if (Post.PART[this.part] === 'Overview') {
        part.index = '1';
        part.name = Post.PART[this.part];
      }

      if (Post.PART[this.part] === 'Work Example') {
        part.index = '2';
        part.name = Post.PART[this.part];
      }

      if (Post.PART[this.part] === 'Reflection') {
        part.index = '3';
        part.name = Post.PART[this.part];
      }
      return part;
    },

    previousPage() {
      return this.$route.query.previousPath;
    },

    breadcrumbs() {
      const { learningUnitId, previousPage = '' } = this;

      const re = new RegExp('/design-planning/info');
      const isDesignPage = previousPage.match(re);

      const route =
        previousPage && isDesignPage && isDesignPage.length
          ? {
              path: this.$route.query.previousPath
            }
          : {
              name: 'design-planning/info',
              params: {
                learningUnitId
              }
            };

      return [
        {
          key: 'design-and-planning',
          name: 'Design and planning',
          to: {
            name: 'design-planning'
          }
        },
        {
          key: 'learning-unit-info',
          name: this.learningUnit?.name || '',
          to: route
        },
        {
          key: 'manage-post',
          name: (this.postId ? 'Edit' : 'Create') + ' task',
          active: true
        }
      ];
    },

    titleErrorMessage() {
      const titleValidate = this.$v.form.title;
      if (!titleValidate.required) {
        return 'The Title is a required field.';
      } else {
        if (!titleValidate.minLength) {
          return 'The Title must be at least 3 characters.';
        } else if (!titleValidate.maxLength) {
          return ' The Title not be greater than 255 characters.';
        } else return '';
      }
    }
  },

  watch: {
    postId: {
      immediate: true,
      handler: 'fetchPost'
    }
  },

  validations: {
    form: {
      title: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(255)
      },
      description: { required },
      word_limit: {
        required: requiredIf((value) => {
          return value.word_limit !== '';
        }),
        minValueValue: minValue(1)
      }
    }
  },

  methods: {
    async fetchCountUserEnrolled() {
      const { learningUnitId } = this;
      try {
        const {
          response: { data = 0 }
        } = await Application.api().count({ learning_unit: learningUnitId });
        this.hasUserEnrolled = data ? true : false;
      } catch (error) {
        this.$toast.error(`Error, please try again.`);
      }
    },

    async fetchPost(postId = null) {
      if (!postId) return;

      this.loading = true;

      try {
        const {
          response: { data }
        } = await Post.api().fetchById(postId);

        if (data && data.criteria && data.criteria.length) {
          this.$set(this.referenceMeta, 'refId', data.id);

          this.form = {
            title: data.title,
            description: data.description,
            word_limit: data.word_limit || ''
          };

          this.criteria = new Criteria({ ...data.criteria[0] });
          this.$set(this.file, 'attachments', data.attachments);

          await this.fetchCountUserEnrolled();
        }
      } catch (error) {
        this.$toast.error(`Cannot fetch Post, please try again.`);
      } finally {
        setTimeout(() => {
          this.loading = false;
        }, 500);
      }
    },

    gotoPreviousPage() {
      if (this.$route.query.previousPath) {
        const { previousPath } = this.$route.query;

        this.$router.push({ path: previousPath });
      } else {
        this.$router.go(-1);
      }
    },

    onAddCriteria() {
      this.triggerValidation = false;
      this.criteria.additional_criteria.push({
        title: '',
        description: ''
      });
    },

    onDeleteCriteria(index, criterion) {
      const { id = null } = criterion;
      if (id) {
        this.additional_criteria_delete_ids.push(id);
      }
      let temp = [...this.criteria.additional_criteria];
      temp.splice(index, 1);
      this.$set(this.criteria, 'additional_criteria', [...temp]);
    },

    async submit() {
      this.triggerValidation = true;
      this.$v.form.$touch();

      this.$nextTick(async () => {
        if (this.$v.form.title.$anyError) {
          return;
        }

        if (this.$v.form.description.$anyError) {
          return;
        }

        if (this.$refs['form-criteria'].$v.criterion.$anyError) {
          return;
        }

        if (this.$v.form.word_limit.$anyError) {
          return;
        }

        try {
          const { learningUnitId = null, postId = null, form, criteria, part } = this;
          const { additional_criteria = [] } = criteria;

          const body = {
            ...form,
            word_limit: form.word_limit !== '' ? form.word_limit : null,
            criterion: criteria,
            additional_criteria,
            learningUnitId,
            part
          };

          if (postId) {
            this.isSubmitting = true;

            await Post.api().updatePostEvaluationAssessments(postId, body);

            if (this.$refs.attachmentUploader.hasAnyDeletedFiles()) {
              await this.$refs.attachmentUploader.deleteRemovedFiles();
            }

            if (this.$refs.attachmentUploader.hasAnyQueuedFiles()) {
              await this.$refs.attachmentUploader.upload();
            }

            this.triggerValidation = false;
            this.isSubmitting = false;
            this.gotoPreviousPage();
          } else {
            this.isSubmitting = true;

            const { response } = await Post.api().createPostEvaluationAssessments(body);

            const { id } = response.data;
            this.$set(this.referenceMeta, 'refId', id);

            if (this.$refs.attachmentUploader.hasAnyQueuedFiles()) {
              await this.$refs.attachmentUploader.upload();
            }

            this.triggerValidation = false;
            this.isSubmitting = false;
            this.gotoPreviousPage();
          }
        } catch (error) {
          //
        }
      });
    }
  }
};
</script>

<style lang="scss">
.input__word-limit {
  min-width: 100px;
}

.ql-editor.ql-blank::before {
  font-family: Heebo;
  font-style: normal;
  font-weight: normal;
  font-size: 15.2px;
  line-height: 22px;
  color: #d1d2db;
}

.ql-editor {
  height: 10rem;
}

.ql-container.ql-snow {
  background: #ffffff;
  border: 1px solid #d1d2db;
  box-sizing: border-box;
  border-radius: 0 0 4.64px 4.64px;
}

.ql-toolbar.ql-snow {
  background: #ffffff;
  border: 1px solid #d1d2db;
  box-sizing: border-box;
  border-radius: 4.64px 4.64px 0 0;
}

.text_editor__required {
  .ql-container.ql-snow {
    background: #ffffff !important;
    border: 1px solid #f83245 !important;
    border-radius: 0 0 4.64px 4.64px !important;
  }

  .ql-toolbar.ql-snow {
    background: #ffffff !important;
    border: 1px solid #f83245 !important;
    border-radius: 4.64px 4.64px 0 0 !important;
    border-bottom: none !important;
  }
}

.text-error {
  color: #f83245;
}
</style>
